import React from 'react';

import { PhoneCallDetailDTO, Company, AgentTypeEnum } from '_lib/api/apiTypes.shim';

import { Accordion } from '_lib/components/Accordion/Accordion';
import { AccordionDetails } from '_lib/components/AccordionDetail/AccordionDetail';
import { AccordionSummary } from '_lib/components/AccordionSummary/AccordionSummary';
import { Box } from '_lib/components/Box/Box';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { UserDisplayDTO } from '_lib/featureComponents/CustomerPhoneCallHistory/CustomerPhoneCallHistory.types';
import { TextBlockTypography } from '_lib/featureComponents/TextBoxTypography/TextBoxTypography';
import { AuthRoles, useAuthRoles } from '_lib/hooks/useAuthRoles';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';

import { getFullName } from '_lib/utils/helpers';

type PhoneCallDisplayCompany = Pick<Company, 'id' | 'name' | 'timezone'>;

export interface PhoneCallListDisplay extends PhoneCallDetailDTO {
  company?: PhoneCallDisplayCompany;
  createdByName?: string;
}

interface PhoneCallListItemProps {
  phoneCall: PhoneCallDetailDTO;
  callCenterUsers: UserDisplayDTO[];
  company: Company;
}

const transformPhoneCall = (
  phoneCall: PhoneCallDetailDTO,
  callCenterUsers: UserDisplayDTO[],
  company: Company,
  authRoles: AuthRoles
): PhoneCallListDisplay => {
  const user = callCenterUsers.find((user) => user.id === phoneCall.createdById);

  let createdByName: string | undefined = undefined;

  // Do not show callCenterAgent to account users
  if (authRoles.isAccountUser && phoneCall.agentType === AgentTypeEnum.CallCenter) createdByName = 'CallCenter Agent';
  // Do not show accountAgent names to non-admins in callPilot
  else if (authRoles.isCallCenterUser && phoneCall.agentType === AgentTypeEnum.Account) createdByName = 'Account Agent';
  // Only show System names to System Users
  else if (!authRoles.isSystem && phoneCall.agentType === AgentTypeEnum.System) createdByName = 'System Agent';
  else if (user && user.isActive) createdByName = getFullName(user);
  else if (user && !user.isActive) createdByName = `${getFullName(user)} (Inactive)`;

  return { ...phoneCall, company, createdByName };
};

export const PhoneCallListItem = (props: PhoneCallListItemProps): React.ReactElement => {
  const { phoneCall, callCenterUsers, company } = props;

  const authRoles = useAuthRoles();

  const formatDateTime = (val: Date | string) =>
    DateUtils.format(val, DateFormatEnum.DateTimeWeekday, { timezone: company.timezone, showTimezone: true });

  const phoneCallDisplay = transformPhoneCall(phoneCall, callCenterUsers, company, authRoles);

  const dateTimeString = formatDateTime(phoneCallDisplay.createdAt);
  const durationString = DateUtils.toPrettyDuration(phoneCallDisplay.duration);
  const callAgent = phoneCallDisplay?.createdByName || 'Unknown Call Agent';

  const appointmentDate = phoneCallDisplay.appointmentDate && formatDateTime(phoneCallDisplay.appointmentDate);

  const previousAppointmentDateDisplay = phoneCallDisplay.previousAppointmentDateTime
    ? formatDateTime(phoneCallDisplay.previousAppointmentDateTime)
    : phoneCallDisplay?.previousAppointmentDate;

  return (
    <Accordion key={phoneCallDisplay.id}>
      <AccordionSummary>
        <Stack spacing={0} sx={{ alignItems: 'flex-start' }}>
          <Typography
            variant='body1Bold'
            text={
              phoneCallDisplay?.originalCompany || `${phoneCallDisplay?.company?.name} (Original Name Not Recorded)`
            }
          />
          <Typography text={callAgent} />
          <Typography text={`${dateTimeString} - ${phoneCallDisplay.outcome}`} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
          <Box>
            <Typography variant='body1Bold' text='Call Agent' />
            <Typography text={callAgent} />
          </Box>

          <Box>
            <Typography variant='body1Bold' text='Call Date & Time' />
            <Typography text={dateTimeString} />
          </Box>
          <Box>
            <Typography variant='body1Bold' text='Duration' />
            <Typography text={durationString} />
          </Box>

          <Box>
            <Typography variant='body1Bold' text='Outcome' />
            <Typography text={phoneCallDisplay.outcome} />
          </Box>

          {appointmentDate && (
            <Box>
              <Typography variant='body1Bold' text='Appointment Date' />
              <Typography text={appointmentDate} />
            </Box>
          )}

          {previousAppointmentDateDisplay && (
            <Box>
              <Typography variant='body1Bold' text='Previous Appointment Date' />
              <Typography text={previousAppointmentDateDisplay} />
            </Box>
          )}

          <Box sx={{ maxWidth: '100%' }}>
            <Typography variant='body1Bold' text='Notes' />
            <TextBlockTypography text={phoneCallDisplay?.notes || 'N/A'} />
          </Box>

          <Box sx={{ maxWidth: '100%' }}>
            <Typography variant='body1Bold' text='Internal Notes' />
            <TextBlockTypography text={phoneCallDisplay?.internalNotes || 'N/A'} />
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
